// Tablero de seguimiento de un atleta — los 7 paneles del plan.
// La vista NO calcula nada: semáforo, carga, caídas %, racha y banda de peso
// vienen del backend (apps.performance.services). Sólo pinta.

import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Area, Bar, BarChart, CartesianGrid, ComposedChart, Line, LineChart,
  ReferenceArea, ReferenceLine, ResponsiveContainer, Tooltip, XAxis, YAxis,
} from 'recharts';
import {
  Activity, CalendarClock, CheckCircle2, Circle, Dumbbell, Flame, Gauge,
  Loader, RefreshCw, Scale, Shield, Target, TrendingUp, XCircle,
} from 'lucide-react';

import { getDashboard, listAthletes, logSparring } from '../api';
import type { Athlete, PerformanceDashboard, Readiness, WeightStatus } from '../types';
import { useModules } from '@/context/ModulesContext';

const READY: Record<Readiness, { label: string; dot: string; ring: string; text: string; hint: string }> = {
  verde:     { label: 'Verde',     dot: 'bg-emerald-500', ring: 'border-emerald-500/40', text: 'text-emerald-400', hint: 'Entrena según el plan.' },
  amarillo:  { label: 'Amarillo',  dot: 'bg-amber-500',   ring: 'border-amber-500/40',   text: 'text-amber-400',   hint: 'Recuperación comprometida: cuida el volumen.' },
  rojo:      { label: 'Rojo',      dot: 'bg-red-500',     ring: 'border-red-500/40',     text: 'text-red-400',     hint: 'Hoy se baja la intensidad.' },
  sin_datos: { label: 'Sin datos', dot: 'bg-muted-foreground', ring: 'border-border',     text: 'text-muted-foreground', hint: 'Falta el check-in de hoy.' },
};

const WEIGHT: Record<WeightStatus['status'], { label: string; text: string }> = {
  dentro:      { label: 'Dentro de su banda', text: 'text-emerald-400' },
  en_el_borde: { label: 'En el borde',        text: 'text-amber-400' },
  fuera:       { label: 'Fuera de la banda',  text: 'text-red-400' },
  sin_limite:  { label: 'Sin límite definido', text: 'text-muted-foreground' },
  sin_dato:    { label: 'Sin peso hoy',        text: 'text-muted-foreground' },
};

const tick = { fontSize: 10, fill: 'currentColor' };
const shortDate = (d: string) => d.slice(5);

function Card({ title, icon, children, className = '' }: {
  title: string; icon: JSX.Element; children: React.ReactNode; className?: string;
}) {
  return (
    <section className={`bg-card border border-border rounded-xl p-4 ${className}`}>
      <h3 className="flex items-center gap-2 text-sm font-semibold text-foreground mb-3">
        <span className="text-muted-foreground">{icon}</span>{title}
      </h3>
      {children}
    </section>
  );
}

function Empty({ children }: { children: React.ReactNode }) {
  return <p className="text-xs text-muted-foreground py-6 text-center">{children}</p>;
}

function Meets({ ok }: { ok: boolean | null }) {
  if (ok === null) return <Circle size={14} className="text-muted-foreground" />;
  return ok ? <CheckCircle2 size={14} className="text-emerald-400" /> : <XCircle size={14} className="text-red-400" />;
}

function SparringForm({ athleteId, onSaved }: { athleteId?: string; onSaved: () => void }) {
  const [f, setF] = useState({ td: '', gu: '', top: '', rounds: '', note: '' });
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const num = (v: string) => (v.trim() === '' ? undefined : Number(v));
  const submit = async () => {
    setBusy(true); setErr(null);
    try {
      await logSparring({
        athlete_id: athleteId,
        takedowns_received_first_min: num(f.td), getup_seconds: num(f.gu),
        top_or_standing_pct: num(f.top), rounds: num(f.rounds), note: f.note || undefined,
      });
      setF({ td: '', gu: '', top: '', rounds: '', note: '' });
      onSaved();
    } catch (e) { setErr((e as Error).message); } finally { setBusy(false); }
  };
  const input = 'w-full bg-background border border-border rounded-md px-2 py-1.5 text-sm text-foreground';
  return (
    <div className="mt-4 border-t border-border pt-3 space-y-2">
      <p className="text-xs font-medium text-foreground">Registrar sparring de hoy</p>
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
        <input className={input} inputMode="numeric" placeholder="Derribos 1er min" value={f.td} onChange={(e) => setF({ ...f, td: e.target.value })} />
        <input className={input} inputMode="decimal" placeholder="Seg. levantarse" value={f.gu} onChange={(e) => setF({ ...f, gu: e.target.value })} />
        <input className={input} inputMode="numeric" placeholder="% arriba / de pie" value={f.top} onChange={(e) => setF({ ...f, top: e.target.value })} />
        <input className={input} inputMode="numeric" placeholder="Rounds" value={f.rounds} onChange={(e) => setF({ ...f, rounds: e.target.value })} />
      </div>
      <input className={input} placeholder="Nota (opcional)" value={f.note} onChange={(e) => setF({ ...f, note: e.target.value })} />
      <div className="flex items-center gap-3">
        <button onClick={submit} disabled={busy}
          className="px-3 py-1.5 rounded-md bg-primary text-primary-foreground text-sm disabled:opacity-50">
          {busy ? 'Guardando…' : 'Guardar'}
        </button>
        {err && <span className="text-xs text-red-400">{err}</span>}
      </div>
    </div>
  );
}

export function PerformanceView() {
  const { userRoles, isAdmin } = useModules();
  const [athletes, setAthletes] = useState<Athlete[]>([]);
  const [athleteId, setAthleteId] = useState<string | undefined>();
  const [data, setData] = useState<PerformanceDashboard | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  const canLogSparring = isAdmin || (userRoles ?? []).some((r) => r === 'entrenador' || r === 'atleta');

  const load = useCallback(async () => {
    setLoading(true); setError(null);
    try {
      const list = await listAthletes();
      setAthletes(list);
      const id = athleteId ?? (list.length > 1 ? list[0].id : undefined);
      if (!athleteId && id) setAthleteId(id);
      setData(await getDashboard(id));
    } catch (e) {
      setError((e as Error).message);
    } finally { setLoading(false); }
  }, [athleteId]);

  useEffect(() => { load(); }, [load]);

  const weightLimit = data?.athlete.weight_limit_kg ?? null;
  const band = data?.athlete.weight_band_kg ?? null;
  const weightSeries = useMemo(() => (data?.checkins ?? []).filter((c) => c.weight_kg !== null), [data]);
  const weightDomain = useMemo(() => {
    const vals = weightSeries.map((c) => c.weight_kg as number);
    if (weightLimit !== null) vals.push(weightLimit, weightLimit + (band ?? 0));
    if (!vals.length) return ['auto', 'auto'] as const;
    return [Math.floor(Math.min(...vals) - 1), Math.ceil(Math.max(...vals) + 1)] as const;
  }, [weightSeries, weightLimit, band]);

  if (loading && !data) {
    return <div className="flex items-center gap-2 p-8 text-muted-foreground"><Loader className="animate-spin" size={16} /> Cargando tablero…</div>;
  }
  if (error) {
    return (
      <div className="p-6">
        <div className="bg-card border border-border rounded-xl p-4 text-sm text-red-400">
          No se pudo cargar el tablero: {error}
        </div>
      </div>
    );
  }
  if (!data) return null;

  const t = data.today;
  const r = READY[t.readiness.status];
  const w = WEIGHT[t.weight.status];
  const sp = data.sparring.summary;

  return (
    <div className="p-4 sm:p-6 space-y-4">
      {/* Encabezado */}
      <header className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="text-xl font-semibold text-foreground">{data.athlete.display_name}</h1>
          <p className="text-sm text-muted-foreground">
            {[data.athlete.target_event, data.athlete.division].filter(Boolean).join(' · ') || 'Seguimiento de rendimiento'}
          </p>
        </div>
        <div className="flex items-center gap-2">
          {athletes.length > 1 && (
            <select value={athleteId} onChange={(e) => setAthleteId(e.target.value)}
              className="bg-background border border-border rounded-md px-2 py-1.5 text-sm text-foreground">
              {athletes.map((a) => <option key={a.id} value={a.id}>{a.display_name}</option>)}
            </select>
          )}
          <button onClick={load} className="p-2 rounded-md border border-border text-muted-foreground hover:text-foreground" title="Actualizar">
            <RefreshCw size={15} className={loading ? 'animate-spin' : ''} />
          </button>
        </div>
      </header>

      {/* 1 · Hoy */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <section className={`bg-card border rounded-xl p-4 ${r.ring}`}>
          <h3 className="flex items-center gap-2 text-sm font-semibold text-foreground mb-2"><Gauge size={15} className="text-muted-foreground" />Hoy</h3>
          <div className="flex items-center gap-3">
            <span className={`w-4 h-4 rounded-full ${r.dot}`} />
            <span className={`text-2xl font-semibold ${r.text}`}>{r.label}</span>
          </div>
          <p className="text-sm text-foreground mt-2">{r.hint}</p>
          {t.readiness.reasons.length > 0 && (
            <ul className="mt-2 space-y-1">
              {t.readiness.reasons.map((x, i) => <li key={i} className="text-xs text-muted-foreground">· {x}</li>)}
            </ul>
          )}
        </section>

        <Card title="Bloque actual" icon={<Target size={15} />}>
          {t.block ? (
            <>
              <p className="text-lg font-semibold text-foreground">{t.block.name}</p>
              <p className="text-xs text-muted-foreground mb-2">Día {t.block.day_in_block} de {t.block.days_in_block}</p>
              <p className="text-sm text-foreground">{t.block.goal}</p>
            </>
          ) : <Empty>Sin bloque para hoy.</Empty>}
        </Card>

        <Card title="Cuenta regresiva" icon={<CalendarClock size={15} />}>
          {t.days_to_target !== null ? (
            <>
              <p className="text-4xl font-semibold text-foreground">{t.days_to_target}</p>
              <p className="text-sm text-muted-foreground">días para {data.athlete.target_event || 'el objetivo'}</p>
            </>
          ) : <Empty>Sin fecha objetivo.</Empty>}
          <p className={`text-xs mt-3 ${t.checkin_done ? 'text-emerald-400' : 'text-amber-400'}`}>
            {t.checkin_done ? 'Check-in de hoy registrado' : 'Check-in de hoy pendiente'}
          </p>
        </Card>
      </div>

      {/* 2 · Peso · 3 · Preparación */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <Card title="Peso" icon={<Scale size={15} />}>
          <p className={`text-xs mb-2 ${w.text}`}>
            {w.label}{t.weight.over_kg !== null && ` · ${t.weight.over_kg > 0 ? '+' : ''}${t.weight.over_kg} kg sobre el límite`}
          </p>
          {weightSeries.length ? (
            <ResponsiveContainer width="100%" height={220} className="text-muted-foreground">
              <LineChart data={weightSeries} margin={{ left: -18, right: 8 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="currentColor" strokeOpacity={0.15} vertical={false} />
                <XAxis dataKey="date" tickFormatter={shortDate} tick={tick} />
                <YAxis domain={weightDomain as never} tick={tick} />
                <Tooltip />
                {weightLimit !== null && band !== null && (
                  <ReferenceArea y1={weightLimit} y2={weightLimit + band} fill="var(--acc1)" fillOpacity={0.08} />
                )}
                {weightLimit !== null && <ReferenceLine y={weightLimit} stroke="currentColor" strokeDasharray="4 4" label={{ value: `límite ${weightLimit}`, fontSize: 10, fill: 'currentColor', position: 'insideBottomRight' }} />}
                <Line type="monotone" dataKey="weight_kg" name="Peso (kg)" stroke="var(--acc1)" strokeWidth={2} dot={{ r: 2 }} />
              </LineChart>
            </ResponsiveContainer>
          ) : <Empty>Sin registros de peso todavía.</Empty>}
        </Card>

        <Card title="Preparación (VFC y salto)" icon={<Activity size={15} />}>
          <p className="text-xs text-muted-foreground mb-2">
            {t.readiness.baseline_ready ? 'Comparado contra su línea base de 7 días.' : 'Línea base en construcción: se necesitan algunos días de registro.'}
          </p>
          {data.checkins.some((c) => c.hrv_ms !== null || c.cmj_cm !== null) ? (
            <ResponsiveContainer width="100%" height={220} className="text-muted-foreground">
              <ComposedChart data={data.checkins} margin={{ left: -18, right: -10 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="currentColor" strokeOpacity={0.15} vertical={false} />
                <XAxis dataKey="date" tickFormatter={shortDate} tick={tick} />
                <YAxis yAxisId="hrv" tick={tick} />
                <YAxis yAxisId="cmj" orientation="right" tick={tick} />
                <Tooltip />
                <Line yAxisId="hrv" type="monotone" dataKey="hrv_ms" name="VFC (ms)" stroke="var(--acc1)" strokeWidth={2} dot={{ r: 2 }} connectNulls />
                <Line yAxisId="cmj" type="monotone" dataKey="cmj_cm" name="Salto (cm)" stroke="currentColor" strokeWidth={1.5} dot={{ r: 2 }} connectNulls />
              </ComposedChart>
            </ResponsiveContainer>
          ) : <Empty>Sin datos de VFC ni salto todavía.</Empty>}
        </Card>
      </div>

      {/* 4 · Carga · 5 · Sparring */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <Card title="Carga (esfuerzo × minutos)" icon={<Dumbbell size={15} />}>
          {data.load_daily.length ? (
            <>
              <ResponsiveContainer width="100%" height={200} className="text-muted-foreground">
                <BarChart data={data.load_daily} margin={{ left: -18, right: 8 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="currentColor" strokeOpacity={0.15} vertical={false} />
                  <XAxis dataKey="date" tickFormatter={shortDate} tick={tick} />
                  <YAxis tick={tick} allowDecimals={false} />
                  <Tooltip />
                  <Bar dataKey="load" name="Carga" fill="var(--acc1)" radius={[3, 3, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
              <div className="flex flex-wrap gap-2 mt-2">
                {data.load_weekly.map((wk) => (
                  <span key={wk.week_start} className="text-xs border border-border rounded-md px-2 py-1 text-muted-foreground">
                    Sem. {shortDate(wk.week_start)}: <span className="text-foreground font-medium">{wk.load.toLocaleString('es-MX')}</span>
                  </span>
                ))}
              </div>
            </>
          ) : <Empty>Sin sesiones registradas.</Empty>}
        </Card>

        <Card title="Sparring (últimos 7 días)" icon={<Shield size={15} />}>
          <div className="grid grid-cols-3 gap-2">
            {[
              { label: 'Derribos 1er min', v: sp.takedowns_received_first_min_avg, goal: `objetivo ≤ ${sp.targets.takedowns_received_first_min_max}`, ok: sp.meets.takedowns },
              { label: 'Seg. levantarse', v: sp.getup_seconds_avg, goal: `objetivo < ${sp.targets.getup_seconds_max}`, ok: sp.meets.getup },
              { label: '% arriba / de pie', v: sp.top_or_standing_pct_avg, goal: `objetivo > ${sp.targets.top_or_standing_pct_min}`, ok: sp.meets.top_or_standing },
            ].map((k) => (
              <div key={k.label} className="border border-border rounded-lg p-2">
                <div className="flex items-center justify-between"><span className="text-[11px] text-muted-foreground">{k.label}</span><Meets ok={k.ok} /></div>
                <p className="text-xl font-semibold text-foreground">{k.v ?? '—'}</p>
                <p className="text-[10px] text-muted-foreground">{k.goal}</p>
              </div>
            ))}
          </div>
          <p className="text-[11px] text-muted-foreground mt-2">{sp.sessions} sparring(s) en la ventana · promedios calculados por el sistema</p>
          {data.sparring.history.length > 0 && (
            <ResponsiveContainer width="100%" height={140} className="text-muted-foreground">
              <ComposedChart data={data.sparring.history} margin={{ left: -18, right: 8, top: 8 }}>
                <XAxis dataKey="date" tickFormatter={shortDate} tick={tick} />
                <YAxis tick={tick} />
                <Tooltip />
                <Area type="monotone" dataKey="top_or_standing_pct" name="% arriba" stroke="var(--acc1)" fill="var(--acc1)" fillOpacity={0.12} />
                <Line type="monotone" dataKey="getup_seconds" name="Seg. levantarse" stroke="currentColor" dot={{ r: 2 }} />
              </ComposedChart>
            </ResponsiveContainer>
          )}
          {canLogSparring && <SparringForm athleteId={athleteId} onSaved={load} />}
        </Card>
      </div>

      {/* 6 · Hábitos · 7 · Tablero 1% */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <Card title="Hábitos" icon={<Flame size={15} />}>
          {data.habits.today_total ? (
            <>
              <div className="flex items-baseline gap-3 mb-3">
                <span className="text-3xl font-semibold text-foreground">{data.habits.streak_days}</span>
                <span className="text-sm text-muted-foreground">días seguidos completos</span>
              </div>
              <p className="text-xs text-muted-foreground mb-2">Hoy: {data.habits.today_done} de {data.habits.today_total}</p>
              <ul className="space-y-1.5">
                {data.habits.habits.map((h) => (
                  <li key={h.id} className="flex items-center gap-2 text-sm">
                    {h.done_today ? <CheckCircle2 size={15} className="text-emerald-400" /> : <Circle size={15} className="text-muted-foreground" />}
                    <span className={h.done_today ? 'text-foreground' : 'text-muted-foreground'}>{h.name}</span>
                  </li>
                ))}
              </ul>
            </>
          ) : <Empty>Sin hábitos definidos.</Empty>}
        </Card>

        <Card title="Tablero 1%" icon={<TrendingUp size={15} />}>
          {data.weekly_reviews.length ? (
            <div className="space-y-3">
              {data.weekly_reviews.map((wr) => (
                <div key={wr.week_start} className="border border-border rounded-lg p-3">
                  <p className="text-xs text-muted-foreground mb-1">Semana del {wr.week_start}</p>
                  <div className="grid grid-cols-2 gap-3 text-sm">
                    <div><p className="text-[11px] text-emerald-400 mb-1">Mejoró</p>{wr.improved.map((x, i) => <p key={i} className="text-foreground">· {x}</p>)}</div>
                    <div><p className="text-[11px] text-amber-400 mb-1">Se ataca</p>{wr.next_focus.map((x, i) => <p key={i} className="text-foreground">· {x}</p>)}</div>
                  </div>
                </div>
              ))}
            </div>
          ) : <Empty>La primera revisión llega el domingo.</Empty>}
        </Card>
      </div>

      {/* Periodización */}
      {data.blocks.length > 0 && (
        <Card title="Periodización" icon={<CalendarClock size={15} />}>
          <ol className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-2">
            {data.blocks.map((b) => {
              const now = t.date >= b.start_date && t.date <= b.end_date;
              return (
                <li key={b.name} className={`border rounded-lg p-3 ${now ? 'border-primary/50 bg-primary/5' : 'border-border'}`}>
                  <p className="text-sm font-medium text-foreground">{b.name}</p>
                  <p className="text-[11px] text-muted-foreground">{shortDate(b.start_date)} → {shortDate(b.end_date)}</p>
                  {b.goal && <p className="text-xs text-muted-foreground mt-1">{b.goal}</p>}
                </li>
              );
            })}
          </ol>
        </Card>
      )}
    </div>
  );
}
